// src/pages/admin/AdminHeroSlides.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, storage } from "../../firebase";
import {
  addDoc, collection, deleteDoc, doc, onSnapshot,
  orderBy, query, serverTimestamp, updateDoc
} from "firebase/firestore";
import {
  getDownloadURL, ref as storageRef, uploadBytesResumable, deleteObject
} from "firebase/storage";

const SLIDES_COL = "homeSlides"; // même collection que le front
const RATIO_DESKTOP = "16/9";     // aperçu admin uniquement
const RATIO_MOBILE  = "9/16";     // aperçu admin uniquement

export default function AdminHeroSlides() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  // Drag & drop
  const [dragIndex, setDragIndex] = useState(null);
  const overIndexRef = useRef(null);

  useEffect(() => {
    // On lit dans l'ordre (croissant)
    const q = query(collection(db, SLIDES_COL), orderBy("order", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const withOrder = arr.filter(s => typeof s.order === "number");
        const noOrder   = arr.filter(s => typeof s.order !== "number");
        setSlides([...withOrder, ...noOrder]);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // prochain ordre (10, 20, 30...)
  const nextOrder = useMemo(() => {
    if (!slides.length) return 10;
    const max = Math.max(...slides.map(s => (typeof s.order === "number" ? s.order : 0)));
    return (isFinite(max) ? max : 0) + 10;
  }, [slides]);

  // ---------- helpers upload ----------
  async function uploadFile(file, onProgress) {
    const isVideo = file.type?.startsWith("video/");
    const folder  = isVideo ? "slides/videos" : "slides/images";
    const path = `${folder}/${Date.now()}_${file.name}`.replace(/\s+/g, "_");
    const sref = storageRef(storage, path);

    const task = uploadBytesResumable(sref, file, { contentType: file.type || undefined });
    await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          if (onProgress) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            onProgress(pct);
          }
        },
        reject,
        resolve
      );
    });
    const url = await getDownloadURL(sref);
    return { url, storagePath: path, mime: file.type || "" };
  }

  // ---------- CREATE ----------
  async function handleCreate(e) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    const legend = form.get("legend")?.toString().trim() || "";
    const cta    = form.get("cta")?.toString().trim() || "";
    const link   = form.get("link")?.toString().trim() || "";

    // Desktop
    const fileSrc = form.get("fileSrc");
    const urlSrc  = form.get("urlSrc")?.toString().trim() || "";

    // Mobile
    const fileMobile = form.get("fileMobile");
    const urlMobile  = form.get("urlMobile")?.toString().trim() || "";

    const duplicate = form.get("duplicateIfMissing") === "on";

    let src = null; let srcStoragePath = null;
    let mobile = null; let mobileStoragePath = null;

    try {
      // reset progress bars
      const pSrc = document.getElementById("upProgSrc");
      const pMob = document.getElementById("upProgMob");
      if (pSrc) pSrc.style.width = "0%";
      if (pMob) pMob.style.width = "0%";

      // Desktop
      if (fileSrc && fileSrc.size > 0) {
        const up = await uploadFile(fileSrc, (pct) => pSrc && (pSrc.style.width = `${pct}%`));
        src = up.url; srcStoragePath = up.storagePath;
      } else if (urlSrc) {
        src = urlSrc;
      }

      // Mobile
      if (fileMobile && fileMobile.size > 0) {
        const up = await uploadFile(fileMobile, (pct) => pMob && (pMob.style.width = `${pct}%`));
        mobile = up.url; mobileStoragePath = up.storagePath;
      } else if (urlMobile) {
        mobile = urlMobile;
      }

      if (!src && !mobile) {
        alert("Ajoutez au moins un média (desktop ou mobile).");
        return;
      }
      if (duplicate) {
        if (!mobile && src)  mobile = src;
        if (!src && mobile)  src    = mobile;
      }

      await addDoc(collection(db, SLIDES_COL), {
        // modèle 1:1 avec ton HeroSection
        src: src || "",
        mobile: mobile || "",
        legend, cta, link,
        // stockage (facultatif, pour delete propre)
        srcStoragePath: srcStoragePath || null,
        mobileStoragePath: mobileStoragePath || null,
        // tri & timestamps
        order: nextOrder,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      e.currentTarget.reset();
      if (pSrc) pSrc.style.width = "0%";
      if (pMob) pMob.style.width = "0%";
    } catch (err) {
      console.error(err);
      alert("Échec de la création du slide.");
    }
  }

  // ---------- UPDATE simples ----------
  async function handleSave(slideId, partial) {
    try {
      await updateDoc(doc(db, SLIDES_COL, slideId), { ...partial, updatedAt: serverTimestamp() });
    } catch (e) {
      console.error(e);
      alert("Échec de la sauvegarde.");
    }
  }

  // ---------- Remplacer un média (fichier) ----------
  async function replaceMediaWithFile(slide, kind, file, progressId) {
    if (!file || !file.size) return;
    const bar = document.getElementById(progressId);
    if (bar) bar.style.width = "0%";
    try {
      const up = await uploadFile(file, (pct) => bar && (bar.style.width = `${pct}%`));
      // supprimer ancien storage si présent
      const prevPath = slide[kind + "StoragePath"];
      if (prevPath) { try { await deleteObject(storageRef(storage, prevPath)); } catch {} }
      await handleSave(slide.id, {
        [kind]: up.url,
        [kind + "StoragePath"]: up.storagePath || null,
      });
      if (bar) bar.style.width = "100%";
      setTimeout(() => { if (bar) bar.style.width = "0%"; }, 600);
    } catch (e) {
      console.error(e);
      alert("Échec du remplacement du média.");
    }
  }

  // ---------- Définir un média (URL) ----------
  async function setMediaUrl(slide, kind, url) {
    await handleSave(slide.id, { [kind]: url, [kind + "StoragePath"]: null });
  }

  // ---------- Clear un média ----------
  async function clearMedia(slide, kind) {
    if (!window.confirm(`Supprimer le média ${kind.toUpperCase()} ?`)) return;
    const prevPath = slide[kind + "StoragePath"];
    try {
      if (prevPath) { try { await deleteObject(storageRef(storage, prevPath)); } catch {} }
      await handleSave(slide.id, { [kind]: "", [kind + "StoragePath"]: null });
    } catch (e) {
      console.error(e);
      alert("Échec de la suppression du média.");
    }
  }

  // ---------- DELETE slide ----------
  async function handleDelete(slide) {
    if (!window.confirm("Supprimer ce slide ?")) return;
    try {
      if (slide.srcStoragePath)    { try { await deleteObject(storageRef(storage, slide.srcStoragePath)); } catch {} }
      if (slide.mobileStoragePath) { try { await deleteObject(storageRef(storage, slide.mobileStoragePath)); } catch {} }
      await deleteDoc(doc(db, SLIDES_COL, slide.id));
    } catch (e) {
      console.error(e);
      alert("Échec de la suppression.");
    }
  }

  // ---------- DnD order ----------
  function onDragStart(idx) { setDragIndex(idx); }
  function onDragOver(e, idx) { e.preventDefault(); overIndexRef.current = idx; }
  async function onDrop() {
    if (dragIndex == null || overIndexRef.current == null || dragIndex === overIndexRef.current) {
      setDragIndex(null); overIndexRef.current = null; return;
    }
    const arr = [...slides];
    const [moved] = arr.splice(dragIndex, 1);
    arr.splice(overIndexRef.current, 0, moved);
    const updates = arr.map((s, i) => ({ id: s.id, order: (i + 1) * 10 }));
    try {
      await Promise.all(updates.map(u => updateDoc(doc(db, SLIDES_COL, u.id), { order: u.order })));
    } catch (e) {
      console.error(e);
      alert("Échec lors du réordonnancement.");
    } finally {
      setDragIndex(null); overIndexRef.current = null;
    }
  }

  const isVideo = (url) => /\.mp4($|\?)/i.test(String(url || ""));

  return (
    <div className="container py-4">
      <h2 className="fw-bold">Admin — Bannières Hero (Desktop & Mobile)</h2>
      <p className="text-muted">Ce module alimente directement <code>HeroSection</code> (champs: <b>src</b>, <b>mobile</b>, <b>legend</b>, <b>cta</b>, <b>link</b>).</p>

      {/* CREATE */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-bold mb-3">Nouveau slide</h5>
          <form onSubmit={handleCreate} className="row g-3">
            {/* Desktop */}
            <div className="col-12">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="mb-0">Média Desktop / Tablet</h6>
                  <span className="badge text-bg-secondary">≥ md</span>
                </div>
                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <label className="form-label">Fichier (vidéo ou image)</label>
                    <input type="file" name="fileSrc" accept="image/*,video/*" className="form-control" />
                    <div className="progress mt-2" style={{ height: 6 }}>
                      <div id="upProgSrc" className="progress-bar" role="progressbar" style={{ width: "0%" }} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">… ou URL directe</label>
                    <input type="url" name="urlSrc" placeholder="https://…" className="form-control" />
                    <div className="form-text">Laissez vide si vous uploadez un fichier.</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile */}
            <div className="col-12">
              <div className="border rounded p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="mb-0">Média Mobile</h6>
                  <span className="badge text-bg-secondary">&lt; md</span>
                </div>
                <div className="row g-2 mt-1">
                  <div className="col-md-6">
                    <label className="form-label">Fichier (vidéo ou image)</label>
                    <input type="file" name="fileMobile" accept="image/*,video/*" className="form-control" />
                    <div className="progress mt-2" style={{ height: 6 }}>
                      <div id="upProgMobile" className="progress-bar" role="progressbar" style={{ width: "0%" }} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">… ou URL directe</label>
                    <input type="url" name="urlMobile" placeholder="https://…" className="form-control" />
                    <div className="form-check mt-2">
                      <input className="form-check-input" type="checkbox" id="duplicateIfMissing" name="duplicateIfMissing" defaultChecked />
                      <label className="form-check-label" htmlFor="duplicateIfMissing">
                        Dupliquer Desktop → Mobile si vide
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Texte & CTA */}
            <div className="col-md-4">
              <label className="form-label">Titre / Légende</label>
              <input name="legend" className="form-control" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Bouton — libellé</label>
              <input name="cta" className="form-control" placeholder="Ex: Explorer" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Bouton — lien</label>
              <input name="link" className="form-control" placeholder="/catalogue" />
            </div>

            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn-success">Créer</button>
            </div>
          </form>
        </div>
      </div>

      {/* LIST */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold mb-0">Slides ({slides.length})</h5>
            <div className="text-muted small">Glissez les cartes pour réordonner, puis lâchez.</div>
          </div>

          {loading ? (
            <div className="alert alert-light border">Chargement…</div>
          ) : slides.length === 0 ? (
            <div className="alert alert-info mb-0">Aucun slide pour le moment.</div>
          ) : (
            <div className="row g-3" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
              {slides.map((s, i) => (
                <div className="col-12" key={s.id}>
                  <div
                    className="card h-100 shadow-sm slide-card"
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={(e) => onDragOver(e, i)}
                  >
                    {/* APERÇUS */}
                    <div className="row g-0 border-bottom">
                      {/* Desktop */}
                      <div className="col-md-6 p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <h6 className="mb-2">Desktop / Tablet</h6>
                          <span className="badge text-bg-secondary">≥ md</span>
                        </div>
                        <div className="media-wrap" style={{ aspectRatio: RATIO_DESKTOP }}>
                          {isVideo(s.src) ? (
                            <video className="w-100 h-100" style={{ objectFit: "cover" }} src={s.src} muted loop />
                          ) : s.src ? (
                            <img src={s.src} alt={s.legend || "slide"} />
                          ) : (
                            <div className="text-muted small">— Aucun média desktop —</div>
                          )}
                          <span className="drag-handle" title="Glisser pour réordonner">⋮⋮</span>
                        </div>

                        <div className="row g-2 mt-2">
                          <div className="col-sm-6">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              className="form-control form-control-sm"
                              onChange={(e) => replaceMediaWithFile(s, "src", e.target.files?.[0], `pSrc-${s.id}`)}
                            />
                            <div className="progress mt-2" style={{ height: 6 }}>
                              <div id={`pSrc-${s.id}`} className="progress-bar" role="progressbar" style={{ width: "0%" }} />
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="input-group input-group-sm">
                              <input
                                type="url"
                                className="form-control"
                                placeholder="URL https://…"
                                defaultValue={s.src || ""}
                                onBlur={(e) => setMediaUrl(s, "src", e.target.value)}
                              />
                              <button className="btn btn-outline-secondary" type="button" onClick={() => setMediaUrl(s, "src", "")}>Vider</button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <button className="btn btn-outline-danger btn-sm" onClick={() => clearMedia(s, "src")}>
                            Supprimer média Desktop
                          </button>
                        </div>
                      </div>

                      {/* Mobile */}
                      <div className="col-md-6 p-3 border-start">
                        <div className="d-flex align-items-center justify-content-between">
                          <h6 className="mb-2">Mobile</h6>
                          <span className="badge text-bg-secondary">&lt; md</span>
                        </div>
                        <div className="media-wrap" style={{ aspectRatio: RATIO_MOBILE }}>
                          {isVideo(s.mobile) ? (
                            <video className="w-100 h-100" style={{ objectFit: "cover" }} src={s.mobile} muted loop />
                          ) : s.mobile ? (
                            <img src={s.mobile} alt={s.legend || "slide"} />
                          ) : (
                            <div className="text-muted small">— Aucun média mobile —</div>
                          )}
                        </div>

                        <div className="row g-2 mt-2">
                          <div className="col-sm-6">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              className="form-control form-control-sm"
                              onChange={(e) => replaceMediaWithFile(s, "mobile", e.target.files?.[0], `pMob-${s.id}`)}
                            />
                            <div className="progress mt-2" style={{ height: 6 }}>
                              <div id={`pMob-${s.id}`} className="progress-bar" role="progressbar" style={{ width: "0%" }} />
                            </div>
                          </div>
                          <div className="col-sm-6">
                            <div className="input-group input-group-sm">
                              <input
                                type="url"
                                className="form-control"
                                placeholder="URL https://…"
                                defaultValue={s.mobile || ""}
                                onBlur={(e) => setMediaUrl(s, "mobile", e.target.value)}
                              />
                              <button className="btn btn-outline-secondary" type="button" onClick={() => setMediaUrl(s, "mobile", "")}>Vider</button>
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <button className="btn btn-outline-danger btn-sm" onClick={() => clearMedia(s, "mobile")}>
                            Supprimer média Mobile
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* META */}
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">Ordre: {typeof s.order === "number" ? s.order : "—"}</span>
                        <a className="small" href={s.src || s.mobile} target="_blank" rel="noreferrer">Ouvrir</a>
                      </div>

                      <div className="row g-2">
                        <div className="col-md-4">
                          <label className="form-label small mb-1">Titre / Légende</label>
                          <input
                            className="form-control form-control-sm"
                            defaultValue={s.legend || ""}
                            onBlur={(e) => handleSave(s.id, { legend: e.target.value })}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small mb-1">Bouton — libellé</label>
                          <input
                            className="form-control form-control-sm"
                            defaultValue={s.cta || ""}
                            onBlur={(e) => handleSave(s.id, { cta: e.target.value })}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small mb-1">Bouton — lien</label>
                          <input
                            className="form-control form-control-sm"
                            defaultValue={s.link || ""}
                            onBlur={(e) => handleSave(s.id, { link: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="mt-3 d-flex justify-content-between">
                        <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(s)}>Supprimer le slide</button>
                        <span className="drag-hint small text-muted">Glisser la carte pour changer l’ordre</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Styles internes */}
      <style>{`
        .media-wrap{ position:relative; background:#f3f4f6; border:1px solid rgba(0,0,0,.06); border-radius:8px; display:flex; align-items:center; justify-content:center; overflow:hidden; }
        .media-wrap img,.media-wrap video{ width:100%; height:100%; object-fit:cover; }
        .drag-handle{ position:absolute; right:8px; top:8px; background:rgba(0,0,0,.55); color:#fff; border-radius:8px; padding:2px 6px; font-weight:700; cursor:grab; user-select:none; }
        .slide-card{ transition: transform .15s ease, box-shadow .15s ease; }
        .slide-card:active{ transform: scale(.995); }
        .drag-hint{ align-self:center; }
      `}</style>
    </div>
  );
}
